import { Button, Checkbox, CircularProgress, TextField } from "@mui/material";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { selectUser } from "../../slices/user";
import api, { CreateUserPayload } from "../../api";
import { showError } from "../../utils/helpers";
import "../NewInvitationForm/new-invitation-form.css";

interface NewUnitFormProps {
  setShowForm: (showForm: boolean) => void;
  extraFunc: () => void;
  selectedUnit?: number | null;
  selectedUnitDocumentId?: string | null;
  isMobile: boolean;
  showForm?: boolean;
}

const NewUnitForm = ({
  setShowForm,
  extraFunc,
  selectedUnit,
  selectedUnitDocumentId,
  isMobile,
  showForm = true,
}: NewUnitFormProps) => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [unitName, setUnitName] = React.useState<string>("");
  const [accessPointsArray, setAccessPointsArray] = React.useState<
    { name: string; venue: string; value: string }[]
  >([]);
  const [unitAccessPoints, setUnitAccessPoints] = React.useState<string[]>([]);
  const [unitUsers, setUnitUsers] = React.useState<number[]>([]);

  const propertyId = user?.property?.id;

  const resetFormDefaults = () => {
    setUnitName("");
    setUnitAccessPoints([]);
    setUnitUsers([]);
  };

  React.useEffect(() => {
    if (!showForm) {
      resetFormDefaults();
      return;
    }

    if (!selectedUnitDocumentId) {
      resetFormDefaults();
      return;
    }

    getInitialEditingData();
    if (selectedUnit) {
      getUnitUsers(selectedUnit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, selectedUnit, selectedUnitDocumentId, propertyId]);

  const getUnitUsers = async (unitId: number) => {
    if (!unitId) return;
    try {
      const response = await api.usersByUnitListRequest(unitId);
      if (response) {
        const currentUsers = response.map((item) => item?.id);
        setUnitUsers(currentUsers);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  React.useEffect(() => {
    getPropertyAccessPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const getPropertyAccessPoints = async () => {
    if (!propertyId) return;
    try {
      setIsLoading(true);
      const response = await api.accessPointsRequest(propertyId, 1, 100);
      if (response) {
        const currentAccessPoints = response.data.map((item) => ({
          name: item?.name,
          venue: item?.vanue_name,
          value: `${item?.id}`,
        }));
        setAccessPointsArray(currentAccessPoints);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  // const accessPointsArray =
  //   user?.access_points.map((item) => {
  //     return {
  //       name: item.name,
  //       venue: item.vanue_name,
  //       value: `${item.id}`,
  //     };
  //   }) || [];

  const disabledCreateUnit = !unitName || unitAccessPoints.length <= 0;

  const getInitialEditingData = async () => {
    if (selectedUnitDocumentId && propertyId) {
      try {
        const response = await api.unitByIdRequest(
          propertyId,
          selectedUnitDocumentId,
        );
        if (response) {
          const currentAccessPoints =
            response?.data?.access_points?.map((item) => `${item?.id}`) ?? [];
          setUnitName(response?.data?.name ?? "");
          setUnitAccessPoints(currentAccessPoints);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const onSummit = async () => {
    const payload = {
      data: {
        name: unitName,
        property: propertyId,
        access_points: unitAccessPoints.map((item) => Number(item)),
      },
    };
    try {
      setIsLoading(true);
      let response;
      if (selectedUnitDocumentId) {
        response = await api.editUnitRequest(selectedUnitDocumentId, payload);
        if (unitUsers.length > 0) {
          for (const user of unitUsers) {
            const payload: CreateUserPayload = {
              access_points: unitAccessPoints.map((item) => Number(item)),
            };
            await api.updateUser(user, payload);
          }
        }
      } else {
        response = await api.createUnitRequest(payload);
      }
      if (response) {
        setShowForm(false);
        extraFunc();
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="border-box flex flex-dir-col gap-4"
      style={{ width: isMobile ? "100%" : 660 }}
    >
      <div
        className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        <div
          className={
            isMobile
              ? "w-100 border-box flex flex-dir-col align-items-start gap-10"
              : "w-100 border-box flex align-items-start gap-10"
          }
        >
          <div className={isMobile ? "w-100" : "w-50"}>
            <p className="t-nowrap body-text-xs mb-3">{labels.unit_name} *</p>
            <TextField
              fullWidth
              placeholder={labels.unit_name}
              value={unitName}
              size="small"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUnitName(event.target.value);
              }}
            />
          </div>
          <div className={isMobile ? "w-100" : "w-50"}>
            <p className="t-nowrap body-text-xs mb-3">
              {labels.access_points} *
            </p>
            <div className="flex flex-dir-col gap-4 access-points-container">
              {accessPointsArray.map((item, index) => (
                <div
                  key={`${index}-${item?.value}`}
                  className="w-100 flex align-items-center gap-2"
                  style={{ minWidth: 0 }}
                >
                  <Checkbox
                    checked={unitAccessPoints.includes(item?.value)}
                    onChange={() => {
                      const dupSelAccessPoints = [...unitAccessPoints];
                      if (dupSelAccessPoints.includes(item?.value)) {
                        const indexValue = dupSelAccessPoints.indexOf(
                          item?.value,
                        );
                        dupSelAccessPoints.splice(indexValue, 1);
                      } else {
                        dupSelAccessPoints.push(item?.value);
                      }
                      setUnitAccessPoints(dupSelAccessPoints);
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p className="body-text-sm">{item?.name}</p>
                    <p className="body-text-xs title-color">
                      {item?.venue}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
        style={{
          backgroundColor: "white",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
        }}
      >
        <div className="w-100" style={{ textAlign: "center" }}>
          <p className="t-nowrap body-text-sm">* {labels.mandatory_fields}</p>
        </div>
        <div className="w-100" style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            disableElevation
            disabled={disabledCreateUnit}
            onClick={isLoading ? () => {} : onSummit}
          >
            {isLoading ? (
              <CircularProgress style={{ color: "white" }} />
            ) : selectedUnitDocumentId ? (
              labels.save_changes
            ) : (
              labels.create_unit
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewUnitForm;
